import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import ChemicalLibraryDialog from "../../chemicalLibrary/ChemicalLibraryDialog";
import LicenseDetailsDialog from "./LicenseDetailsDialog";
import UserProfileItem from "./UserProfileItem";

import {
  APP_ROUTES,
  HOME_PATH,
  QUERY_PARAMS,
} from "../../../constants/global.constants";

import {
  AdminIcon,
  ChevronRight,
  CostsIcon,
  CustomerServiceIcon,
  ExitIcon,
  ExperimentIcon,
  FileLockIcon,
  NewBadgeIcon,
  ReadIcon,
  UrlOpenIcon,
} from "../../../assets/icons";

import { combineClassNames } from "../../../utils/appUtils";
import { hasLicensedDocumentationAccess } from "../../../utils/LicenseUtils";
import {
  getActiveLanguage,
  getContactUsUrlByLanguage,
} from "../../../utils/Utils";

import "./index.scss";

const UserProfile = ({
  userName,
  userRole,
  userLicenses = [],
  onManageAccess,
  onManageUser,
  onManageLicense,
  onDocumentationClick,
  onSignOut,
  isPathIncluded,
  onAvatarHover,
  onUtilityCosts,
  isForcedOpen = false,
  disableActions = false,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [profileOpen, setProfileOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [chemicalLibraryOpen, setChemicalLibraryOpen] = useState(false);
  const [licenseDetailsOpen, setLicenseDetailsOpen] = useState(false);

  const userManualUrl = process.env.REACT_APP_USER_MANUAL_URL;
  const activeLanguage = getActiveLanguage(i18n);
  const contactUsUrl = getContactUsUrlByLanguage(activeLanguage);

  const isAdmin = userRole === "admin";
  const canAccessDocumentation = useMemo(
    () => hasLicensedDocumentationAccess(userLicenses),
    [userLicenses],
  );

  const initials = useMemo(() => {
    const parts = (userName || "")
      .split(" ")
      .map((namePart) => namePart.trim())
      .filter(Boolean);

    if (!parts.length) {
      return "U";
    }

    return parts
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");
  }, [userName]);

  const closeMenus = () => {
    if (isForcedOpen) {
      return;
    }
    setProfileOpen(false);
    setAdminMenuOpen(false);
  };

  useEffect(() => {
    setProfileOpen(isForcedOpen);
  }, [isForcedOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeMenus();
      }
    };

    if (profileOpen || adminMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileOpen, adminMenuOpen, isForcedOpen]);

  // Handle query parameter to automatically open the license details modal
  // used in license grant email notifications
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const openTarget = searchParams.get(QUERY_PARAMS.OPEN);

    if (
      openTarget !== QUERY_PARAMS.MY_LICENSES ||
      location.pathname !== HOME_PATH
    ) {
      return;
    }

    setLicenseDetailsOpen(true);
    setProfileOpen(false);
    setAdminMenuOpen(false);

    // navigate to home to prevent re-opening on page reload/browser navigation.
    navigate(HOME_PATH, { replace: true });
  }, [location.search, location.pathname]);

  const handleProfileToggle = () => {
    setProfileOpen((prev) => {
      if (prev) {
        setAdminMenuOpen(false);
      }
      return !prev;
    });
  };

  const handleAdminSettingsMouseEnter = () => {
    setAdminMenuOpen(true);
  };

  const handleAdminSettingsMouseLeave = () => {
    setAdminMenuOpen(false);
  };

  const handleManageAccessClick = () => {
    onManageAccess?.();
    closeMenus();
  };

  const handleManageUserClick = () => {
    onManageUser?.();
    closeMenus();
  };

  const handleManageLicenseClick = () => {
    onManageLicense?.();
    closeMenus();
  };

  const handleUtilityCostsClick = () => {
    onUtilityCosts?.();
    closeMenus();
  };

  const handleSignOutClick = () => {
    onSignOut?.();
    closeMenus();
  };

  const handleDocumentationClick = () => {
    onDocumentationClick?.();
    closeMenus();
  };

  const handleExternalLinkClick = (url) => {
    if (!url) {
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    closeMenus();
  };

  const handleChemicalLibraryClick = () => {
    setChemicalLibraryOpen(true);
    closeMenus();
  };

  const handleChemicalLibraryClose = () => {
    setChemicalLibraryOpen(false);
  };

  const handleProfileSummaryClick = () => {
    setLicenseDetailsOpen(true);
    closeMenus();
  };
  const handleLicenseDetailsClose = () => {
    setLicenseDetailsOpen(false);
  };

  return (
    <div
      id="user-profile"
      className="user-profile-container"
      ref={containerRef}
    >
      <div
        id="user-profile-avatar"
        className={combineClassNames(
          "user-profile-avatar",
          disableActions ? "user-profile-avatar--disabled" : "pointer",
        )}
        onMouseEnter={disableActions ? undefined : onAvatarHover}
        onClick={disableActions ? undefined : handleProfileToggle}
      >
        {initials}
      </div>

      {profileOpen && (
        <div className="user-profile-dropdown">
          <div className="user-profile-summary">
            <div className="user-profile-avatar">{initials}</div>
            <div className="user-profile-summary-content">
              <span className="user-profile-name">
                {userName || t("userProfile.userFallback")}
              </span>
              <button
                type="button"
                className="user-profile-view-my-licenses"
                onClick={handleProfileSummaryClick}
              >
                {t("userProfile.viewMyLicenses")}
              </button>
            </div>
          </div>

          <div className="user-profile-divider" />

          <UserProfileItem
            id="user-chemical-library"
            icon={ExperimentIcon}
            label={t("userProfile.chemicalLibrary")}
            onClick={handleChemicalLibraryClick}
          />
          <UserProfileItem
            id="user-utility-costs"
            icon={CostsIcon}
            label={t("userProfile.utilityCosts")}
            onClick={handleUtilityCostsClick}
          />
          {/* <UserProfileItem
            icon={LocaleIcon}
            label={t("userProfile.currencyAndUnits")}
          /> */}

          {isAdmin && (
            <div
              className={"user-profile-admin-settings"}
              onMouseEnter={handleAdminSettingsMouseEnter}
              onMouseLeave={handleAdminSettingsMouseLeave}
            >
              <UserProfileItem
                icon={AdminIcon}
                label={t("userProfile.adminSettings")}
                trailingIcon={ChevronRight}
                className="clickable"
              />

              {adminMenuOpen && (
                <div className="admin-menu-dropdown user-profile-admin-menu">
                  <div
                    className={`admin-menu-item ${isPathIncluded(APP_ROUTES.ADMIN_PORTAL) ? "disabled" : ""}`}
                    onClick={handleManageAccessClick}
                  >
                    {t("adminPage.manageAcess")}
                  </div>
                  <div
                    className={`admin-menu-item ${isPathIncluded(APP_ROUTES.USER_MANAGEMENT) ? "disabled" : ""}`}
                    onClick={handleManageUserClick}
                  >
                    {t("adminPage.userManagement")}
                  </div>
                  <div
                    className={`admin-menu-item ${isPathIncluded(APP_ROUTES.LICENSE_MANAGEMENT) ? "disabled" : ""}`}
                    onClick={handleManageLicenseClick}
                  >
                    {t("licenseManagement.title")}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="user-profile-divider" />

          {canAccessDocumentation && (
            <UserProfileItem
              icon={FileLockIcon}
              label={
                <span className="new-badge-inline-wrapper">
                  <span>{t("userProfile.licensedFeaturesManual")}</span>
                  <NewBadgeIcon />
                </span>
              }
              trailingIcon={UrlOpenIcon}
              onClick={handleDocumentationClick}
            />
          )}

          <UserProfileItem
            icon={ReadIcon}
            label={t("header.userManual")}
            trailingIcon={UrlOpenIcon}
            onClick={() => handleExternalLinkClick(userManualUrl)}
          />

          <UserProfileItem
            icon={CustomerServiceIcon}
            label={t("userProfile.contactSupport")}
            trailingIcon={UrlOpenIcon}
            onClick={() => handleExternalLinkClick(contactUsUrl)}
          />

          <div className="user-profile-divider" />

          <UserProfileItem
            id="user-sign-out"
            icon={ExitIcon}
            className="user-profile-sign-out"
            label={t("userProfile.signOut")}
            onClick={handleSignOutClick}
          />
        </div>
      )}

      {chemicalLibraryOpen && (
        <ChemicalLibraryDialog onClose={handleChemicalLibraryClose} />
      )}

      {licenseDetailsOpen && (
        <LicenseDetailsDialog onClose={handleLicenseDetailsClose} />
      )}
    </div>
  );
};

export default UserProfile;
